import { Box, Button, Card, CardContent, Grid } from "@mui/material";
import { Clear, Search, Undo } from "@mui/icons-material";
import { GridActionsCellItem, GridRowId, GridRowModel, GridSortDirection, GridSortModel } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";

import AutoComplete from "../../../components/hookForms/autoComplete";
import ConfirmationDialog from "../../../components/confirmationDialog";
import { Container } from "../../../theme/globalStyles";
import DataTable from "../../../components/dataTable";
import DatePickerMui from "../../../components/hookForms/datePicker";
import InputNumber from "../../../components/hookForms/inputNumber";
import Tooltip from "@mui/material/Tooltip";
import { format } from "date-fns";
import messages from "../../../utils/messages";
import serviceHistoricoSaldoInstalacao from "../../../services/serviceHistoricoSaldoInstalacao";
import { toast } from "react-toastify";
/* eslint-disable react-hooks/exhaustive-deps */
import { useAuth } from "../../../context/global";
import { useForm } from "react-hook-form";

function HistoricoSaldo(props: any) {
    const { setTitlePage } = useAuth();

    useEffect(() => {
        setTitlePage("Faturamento - Histórico de Saldo");
    }, [setTitlePage]);

    const [data, setData] = useState<any[]>([]);
    const [pageSize, setPageSize] = useState(100);
    const [page, setPage] = useState(0);
    const [filters, setFilters] = useState(null);
    const [rowCount, setRowCount] = useState(0);
    const [isLoading, setLoading] = useState(false);
    const [undoState, setUndoState] = useState({ data: "", open: false });

    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: "",
            sort: "" as GridSortDirection,
        },
    ]);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
        // eslint-disable-next-line react-hooks/rules-of-hooks
    } = useForm({
        defaultValues: {
            tipoSaldo: "",
            numeroInstalacao: "",
            mesUltimaAtualizacao: null,
        },
    });

    const actionColumns = useMemo(
        () => [
            {
                headerName: "Operação",
                field: "operations",
                type: "actions",
                width: 200,
                hide: false,
                getActions: (params: { id: GridRowId; row: GridRowModel }) => [
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Desfazer">
                                <Undo />
                            </Tooltip>
                        }
                        label="Desfazer"
                        onClick={() => setUndoState({ data: params.id.toString(), open: true })}
                        disabled={params.row.permiteExclusao !== "Sim"}
                    />,
                ],
            },
        ],
        []
    );

    const columns = useMemo(
        () => [
            {
                headerName: "ID",
                field: "historicoSaldoInstalacaoID",
                type: "number",
                headerAlign: "left",
                identity: true,
                width: 80,
            },
            {
                headerName: "Instalação",
                field: "numeroInstalacao",
                type: "string",
                width: 180,
            },
            { headerName: "Tipo", field: "tipoSaldo", type: "string", width: 160 },
            { headerName: "Evento", field: "eventoHistoricoSaldo", type: "string", width: 220 },
            {
                headerName: "Anterior",
                field: "saldoAnterior",
                type: "number",
                headerAlign: "left",
                width: 110,
            },
            {
                headerName: "Atual",
                field: "saldoAtual",
                type: "number",
                headerAlign: "left",
                width: 110,
            },
            {
                headerName: "Quantidade",
                field: "quantidadeEvento",
                type: "string",
                width: 150,
            },
            {
                headerName: "Criação",
                field: "dataHoraUltimaAtualizacao",
                type: "date",
                width: 110,
            },
            {
                headerName: "Ciclo",
                field: "cicloFaturamento",
                type: "string",
                width: 80,
            },
            { headerName: "Usina", field: "nomeUsina", type: "string", width: 230 },
            { headerName: "Concessionária", field: "nomeConcessionaria", type: "string", width: 180 },
            ...actionColumns,
        ],
        [actionColumns]
    );

    const tipoSaldoOptions = [
        { label: "Todos", value: "Todos" },
        { label: "Reenergisa", value: "Reenergisa" },
        { label: "Outras Empresas", value: "Outras Empresas" },
        { label: "Concessionária", value: "Concessionária" },
    ];

    const getAllWithFilters = async (filters: any) => {
        setData([]);
        setLoading(true);

        serviceHistoricoSaldoInstalacao
            .getWithFilter({
                ...filters,
                take: pageSize,
                currentPage: page + 1,
                sortExpression: sortModel[0]?.field ?? "",
                sortDirection: sortModel[0]?.sort ?? "",
            })
            .then((result) => {
                setData(result?.data.content);
                setRowCount(result?.data.numberOfElements);
            })
            .catch((err) => {
                toast.error(messages.getWithFilterError, err);
                setData([]);
                setRowCount(0);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(
        () => {
            if (props && props.location && props.location.state) {
                getAllWithFilters(props.location.state);
            } else {
                getAllWithFilters(filters);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [page, pageSize, sortModel]
    );

    const handleUndo = async () => {
        serviceHistoricoSaldoInstalacao
            .undo(undoState.data)
            .then((result) => {
                if (result) {
                    toast.success(messages.undoSucess);
                }
            })
            .catch((err) => {
                toast.error(messages.undoError, err);
            })
            .finally(() => {
                setUndoState({ data: "", open: false });
                getAllWithFilters(filters);
            });
    };

    const onSubmit = (values: any) => {
        const newFilters = {
            ...values,
            tipoSaldo: values.tipoSaldo === "Todos" ? "" : values.tipoSaldo,
            mesUltimaAtualizacao: values.mesUltimaAtualizacao ? format(values.mesUltimaAtualizacao, "MM/yyyy") : "",
        };

        setFilters(newFilters);
        getAllWithFilters(newFilters);
    };

    useEffect(() => {
        if (props && props.location && props.location.state) {
            reset(props.location.state);
            setFilters(props.location.state);
            getAllWithFilters(props.location.state);
        }
    }, [props]);

    const onClear = () => {
        reset({ numeroInstalacao: "" });
        setFilters(null);
        getAllWithFilters(null);
    };

    return (
        <Container>
            <Card>
                <CardContent>
                    <Box sx={{ mb: 2 }}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={2}>
                                <Grid item md={12}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={2}>
                                            <InputNumber
                                                name="numeroInstalacao"
                                                label="Instalação"
                                                control={control}
                                                error={errors}
                                                decimalScale={0}
                                                noThousandSeparator
                                                maxLength={15}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={3}>
                                            <AutoComplete
                                                name="tipoSaldo"
                                                label="Tipo de saldo"
                                                options={tipoSaldoOptions}
                                                control={control}
                                                error={errors}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={3}>
                                            <DatePickerMui
                                                name="mesUltimaAtualizacao"
                                                label="Data de criação"
                                                format="MM/yyyy"
                                                view={["month", "year"]}
                                                control={control}
                                                error={errors}
                                            />
                                        </Grid>

                                        <Grid item md={2}>
                                            <Button
                                                disableElevation
                                                variant="contained"
                                                color="warning"
                                                fullWidth
                                                style={{ margin: "16px 0px 8px 0px" }}
                                                startIcon={<Search />}
                                                type="submit"
                                                disabled={isLoading}
                                            >
                                                Pesquisar
                                            </Button>
                                        </Grid>
                                        <Grid item md={2}>
                                            <Button
                                                disableElevation
                                                variant="outlined"
                                                color="warning"
                                                fullWidth
                                                style={{ margin: "16px 0px 8px 0px" }}
                                                startIcon={<Clear />}
                                                onClick={() => onClear()}
                                            >
                                                Limpar
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </form>
                    </Box>

                    <Box sx={{ height: 450 }}>
                        <DataTable
                            columns={columns}
                            rows={data}
                            rowId={(row: GridRowModel) => row.historicoSaldoInstalacaoID}
                            rowCount={rowCount}
                            isLoading={isLoading}
                            onPageChange={(currentPage: number) => setPage(currentPage)}
                            onPageSizeChange={(currentPageSize: number) => setPageSize(currentPageSize)}
                            pageSize={pageSize}
                            onSortModelChange={(model: GridSortModel) => setSortModel(model)}
                        />
                    </Box>

                    <ConfirmationDialog
                        open={undoState?.open}
                        title={"Desfazer"}
                        message={messages.undoConfirmation}
                        cancelButtonText={"Não"}
                        confirmButtonText={"Sim"}
                        onClose={() => setUndoState({ data: "", open: false })}
                        onCancel={() => setUndoState({ data: "", open: false })}
                        onConfirm={() => {
                            setUndoState({ data: "", open: false });
                            handleUndo();
                        }}
                    />
                </CardContent>
            </Card>
        </Container>
    );
}

export default HistoricoSaldo;
